import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { browserHistory } from 'react-router';
import { reduxForm, SubmissionError, hasSubmitSucceeded, isSubmitting, autofill, initialize } from 'redux-form';
import { compose, lifecycle } from 'recompose';
import { requestAsync } from 'redux-query';
import { RaisedButton, RadioButtonGroup, RadioButton, SelectField, MenuItem, LinearProgress } from 'material-ui';
import ArrowRight from 'material-ui/svg-icons/navigation/chevron-right';
import WarningIcon from 'material-ui/svg-icons/alert/warning';
import _ from 'lodash';
import moment from 'moment-timezone';
import cfg from 'cfg';
import { CAMPAIGN_FORM_ID, CAMPAIGN_FORM_KEYS as FORM_KEYS } from 'dash-front/constants';
import { RowSection } from '../../components/helpers';
import { createPostRequest, handleServerError } from 'dash-front/actions/helpers';
import { fetchCampaigns, fetchCampaignById } from 'dash-front/campaign/actions/campaigns';
import { getEntities, getQueries } from 'dash-front/selectors';
import { mapData, defValidate } from './schema';
import CampaignForm from './CampaignForm';
import auth from 'dash-front/auth';
import { responseStatusIsOk } from 'dash-front/helpers';

const StrategySelect = connect(
    state => ({
        campaigns: getEntities(state).campaigns,
        campaignsPending: _.get(getQueries(state)['GET:campaigns'], 'isPending'),
        campaignsReqStatus: _.get(getQueries(state)['GET:campaigns'], 'status')
    }),
    dispatch => ({
        fetchCampaigns: () => dispatch(fetchCampaigns())
    })
)(
    class _StrategySelect extends React.PureComponent {
        state = { strategy: null, campaigns: [], campaign: null };

        componentDidMount() {
            this.props.fetchCampaigns();
            if (this.props.campaigns) {
                this.updateCampaigns(this.props.campaigns);
            }
        }

        componentWillReceiveProps(props) {
            if (this.props.campaigns !== props.campaigns) {
                this.updateCampaigns(props.campaigns);
            }
        }

        updateCampaigns = campaignsMap => {
            const array = _.isObject(campaignsMap)
                ? Object.keys(campaignsMap)
                      .map(id => campaignsMap[id])
                      .sort((a, b) => a.createdAt - b.createdAt)
                : [];
            this.setState({ campaigns: array });
        };

        onStrategyChange = (e, value) => {
            this.setState({ strategy: value });
        };

        onCampaignChange = (e, index, campaign) => {
            this.setState({ campaign });
        };

        goNext = () => {
            const { campaign, strategy } = this.state;
            const payload = { strategy };
            if (strategy === 'clone') {
                payload.campaign = campaign;
            }
            this.props.onSelect(payload);
        };

        renderCampaignSelect() {
            const { campaigns, campaign } = this.state;
            const { campaignsPending, campaignsReqStatus } = this.props;
            if (campaignsPending) {
                return (
                    <div style={{ marginTop: 12 }}>
                        <LinearProgress />
                    </div>
                );
            }
            if (!responseStatusIsOk(campaignsReqStatus)) {
                return (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            flexDirection: 'column'
                        }}
                    >
                        <WarningIcon />
                        <p>Error while fetching campaigns</p>
                        <p>Status code: {campaignsReqStatus}</p>
                    </div>
                );
            }
            return (
                <SelectField value={campaign} floatingLabelText={'Select campaign'} onChange={this.onCampaignChange}>
                    {campaigns.map(campaign => (
                        <MenuItem key={campaign._id} value={campaign._id} primaryText={campaign.title} />
                    ))}
                </SelectField>
            );
        }

        render() {
            const { campaigns, strategy, campaign } = this.state;
            const canGoNext = strategy === 'empty' || (strategy === 'clone' && Boolean(campaign));
            return (
                <div>
                    <h3>Create campaign</h3>
                    <RadioButtonGroup name={'switch'} onChange={this.onStrategyChange}>
                        <RadioButton value={'empty'} label={'new'} />
                        <RadioButton value={'clone'} label={'based on existing'} />
                    </RadioButtonGroup>
                    {strategy === 'clone' ? this.renderCampaignSelect() : null}
                    <div style={{ marginTop: 12 }}>
                        <RaisedButton
                            primary
                            label={'Next'}
                            labelPosition={'before'}
                            icon={<ArrowRight />}
                            onTouchTap={this.goNext}
                            disabled={!canGoNext}
                        />
                    </div>
                </div>
            );
        }
    }
);

StrategySelect.propTypes = {
    onSelect: PropTypes.func.isRequired
};

class Scene extends React.PureComponent {
    componentDidMount() {
        this.updateStateByQuery(this.props.location.query);
    }

    componentWillReceiveProps({ location }) {
        if (this.props.location !== location) {
            const { query } = location;
            this.updateStateByQuery(query);
        }
    }

    updateStateByQuery = (query = {}) => {
        let { campaign, strategy } = query;
        this.setState({ errorMessage: null, loading: false });
        if (strategy) {
            if (!campaign) {
                strategy = 'empty';
            }
            this.setState({ strategy: strategy });
            if (campaign && strategy === 'clone') {
                this.setState({ loading: true });
                this.props
                    .initializeFromCampaign(campaign)
                    .then(() => this.setState({ loading: false }))
                    .catch(error => {
                        this.setState({
                            errorMessage: error.message,
                            loading: false
                        });
                    });
            } else {
                this.props.initialize({});
            }
        } else {
            this.setState({ strategy });
        }
    };

    state = { strategy: null, loading: false, errorMessage: null };

    onStrategySelect = payload => {
        browserHistory.push({
            pathname: window.location.pathname,
            query: payload
        });
    };

    render() {
        const { strategy, loading, errorMessage } = this.state;
        return (
            <div className="wdp-container">
                {!strategy ? (
                    <StrategySelect onSelect={this.onStrategySelect} />
                ) : (
                    <div>
                        {loading ? <LinearProgress /> : null}
                        {errorMessage ? (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexDirection: 'column'
                                }}
                            >
                                <WarningIcon />
                                <p>{errorMessage}</p>
                            </div>
                        ) : null}
                        <CampaignForm />
                        <RowSection
                            hideSplit
                            style={{ marginBottom: 16 }}
                            rightContent={<RaisedButton primary label={'Save'} onTouchTap={this.props.submit} />}
                        />
                    </div>
                )}
            </div>
        );
    }
}

Scene.propTypes = {
    submit: PropTypes.func.isRequired,
    initializeFromCampaign: PropTypes.func.isRequired
};

/**
 * This bullshit will be fixed.
 * Should do this due to the stepper.
 */
const onSubmit = (values, dispatch, self) => {
    const { syncErrors } = self;
    const errorKeys = Object.keys(syncErrors || {});
    if (errorKeys.length === 0) {
        const mappedData = mapData(values);
        const url = `${cfg.campaign_manager.host}/api/campaigns?access_token=${auth.getAccessToken()}`;
        const params = {
            body: mappedData,
            url
        };
        const config = createPostRequest(params);
        return dispatch(requestAsync(config)).then(handleServerError);
    } else {
        console.log('Invalid in wizzard');
        throw new SubmissionError({ _error_: 'Check your rules' });
    }
};

const getCampaign = campaignId => (dispatch, getState) => {
    const campaign = _.get(getEntities(getState()), ['campaigns', campaignId]);
    return campaign
        ? Promise.resolve(campaign)
        : dispatch(fetchCampaignById(campaignId)).then(({ body }) => {
              if (body.campaign) {
                  return body.campaign;
              }
              throw new Error('No campaign found');
          });
};

const initFromCampaign = (campaignId, form) => (dispatch, getState) => {
    return dispatch(getCampaign(campaignId)).then(campaign => {
        const copy = _.omit(_.extend({}, campaign, { active: false }), ['_id', 'createdAt', 'updatedAt', 'stats']);
        copy.title = copy.title + ' copy';
        dispatch(initialize(form, copy));
    });
};

const mapStateToProps = state => {
    return {
        submitSucceeded: hasSubmitSucceeded(CAMPAIGN_FORM_ID)(state),
        submitting: isSubmitting(CAMPAIGN_FORM_ID)(state)
    };
};

const mapDispatchToProps = (dispatch, { form }) => {
    return {
        initializeFromCampaign: id => dispatch(initFromCampaign(id, form)),
        dispatch
    };
};

const CreateEnhanced = compose(
    reduxForm({
        form: CAMPAIGN_FORM_ID,
        onSubmit,
        validate: defValidate
    }),
    connect(mapStateToProps, mapDispatchToProps),
    lifecycle({
        componentWillReceiveProps({ submitSucceeded }) {
            if (this.props.submitting && submitSucceeded) {
                browserHistory.push('/campaigns');
            }
        },
        componentDidMount() {
            const { dispatch } = this.props;
            const tz = moment.tz.guess();
            dispatch(autofill(CAMPAIGN_FORM_ID, FORM_KEYS.TIMEZONE, tz));
        }
    })
)(Scene);

export default CreateEnhanced;



/** WEBPACK FOOTER **
 ** ../src/dash/front/campaign/containers/Campaign/Create.js
 **/